import { change, DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeBriefBackground } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';
import { FieldSelectList } from '../../../../../components/redux-form/fields/FieldSelectList';
import { FieldTextArea } from '../../../../../components/redux-form/fields/FieldTextArea';

export interface BriefBackgroundFormData {
    student: string;
    formId: string;

    mainConcern: string;
    educationalHistory: string;
    previousExperience: string;
    currentTherapies: string;
    strength: string;
    possibleReinforcers: string;

    communications: [];
    behaviors: [];
    interests: [];
    socials: [];
    schedule: [];
}

interface BriefBackgroundFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<BriefBackgroundFormData, BriefBackgroundFormProps>;

const validators: Validators<BriefBackgroundFormData> = {
    mainConcern: [maxLength({ max: 2000 })],
    educationalHistory: [maxLength({ max: 2000 })],
    previousExperience: [maxLength({ max: 2000 })],
    currentTherapies: [maxLength({ max: 2000 })],
    strength: [maxLength({ max: 2000 })],
    possibleReinforcers: [maxLength({ max: 2000 })],
};

const BriefBackgroundForm: React.FunctionComponent<InjectedProps & BriefBackgroundFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & BriefBackgroundFormProps) => {
    const communicationsValues: SelectObject[] = [
        { label: 'Delayed speech and language skills', value: 1 },
        { label: 'Repeats words or phrases over and over (echolalia)', value: 2 },
        { label: 'Reverses pronouns (e.g., says "you" instead of "I")', value: 3 },
        { label: 'Gives unrelated answers to questions', value: 4 },
        { label: 'Does not point or respond to pointing', value: 5 },
        { label: 'Uses few or no gestures (e.g., does not wave goodbye)', value: 6 },
        { label: 'Talks in a flat, robot-like, or sing-song voice', value: 7 },
        { label: 'Does not pretend in play (e.g., does not pretend to "feed" a doll)', value: 8 },
        { label: 'Does not understand jokes, sarcasm, or teasing', value: 9 },
    ];

    const behaviorsValues: SelectObject[] = [
        { label: 'Hyperactivity (very active)', value: 1 },
        { label: 'Impulsivity (acting without thinking)', value: 2 },
        { label: 'Short attention span', value: 3 },
        { label: 'Aggression', value: 4 },
        { label: 'Causing self-injury', value: 5 },
        { label: 'Temper tantrums', value: 6 },
        { label: 'Unusual eating habits', value: 7 },
        { label: 'Unusual sleeping habits', value: 8 },
        { label: 'Unusual mood or emotional reactions', value: 9 },
        { label: 'Lack of fear or more fear than expected', value: 10 },
        { label: 'Unusual reactions to the way things sound, smell, taste, look, or feel', value: 11 },
        { label: 'Toileting issues', value: 12 },
    ];

    const interestsValues: SelectObject[] = [
        { label: 'Lines up toys or other objects', value: 1 },
        { label: 'Plays with toys the same way every time', value: 2 },
        { label: 'Likes parts of objects (e.g., wheels)', value: 3 },
        { label: 'Is very organized', value: 4 },
        { label: 'Gets upset by minor changes', value: 5 },
        { label: 'Has obsessive interests', value: 6 },
        { label: 'Has to follow certain routines', value: 7 },
        { label: 'Flaps hands, rocks body, or spins self in circles', value: 8 },
    ];

    const socialsValues: SelectObject[] = [
        { label: 'Avoids eye-contact', value: 1 },
        { label: 'Prefers to play alone', value: 2 },
        { label: 'Does not share interests with others', value: 3 },
        { label: 'Only interacts to achieve a desired goal', value: 4 },
        { label: 'Has flat or inappropriate facial expressions', value: 5 },
        { label: 'Does not understand personal space or boundaries', value: 6 },
        { label: 'Avoids or resists physical contact', value: 7 },
        { label: 'Is not comforted by others during distress', value: 8 },
        { label: "Has trouble understanding other people's feelings or talking about own feelings", value: 9 },
    ];

    const scheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const scheduleTime = ['Morning', 'Afternoon', 'Evening'];

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA: Brief Background Information</h2>
            <h3 className={classNames(styles.subTitle)}>(this will be expanded upon during the parent intake)</h3>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={3}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldTextArea
                name="mainConcern"
                //type="text"
                validate={validators.mainConcern}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'What are your current main concerns (social, communication, academic, behavioral)?',
                }}
            />
            <FieldTextArea
                name="educationalHistory"
                validate={validators.educationalHistory}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please describe the patients’ educational history. ',
                }}
            />
            <FieldTextArea
                name="previousExperience"
                //type="text"
                validate={validators.previousExperience}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please describe any previous ABA experience (positive and/or negative).',
                }}
            />
            <FieldTextArea
                name="currentTherapies"
                //type="text"
                validate={validators.currentTherapies}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'Please list all current therapies (OT, PT, Speech, etc.), including frequency and length of sessions.',
                }}
            />
            <FieldTextArea
                name="strength"
                //type="text"
                validate={validators.strength}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label:
                        'Please describe his/her strengths (hobbies, skills, socially, level of communication, academic interests, etc.)',
                }}
            />
            <FieldTextArea
                name="possibleReinforcers"
                //type="text"
                validate={validators.possibleReinforcers}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'What are some possible reinforcers (food, toys, physical contact, activities, other)?',
                }}
            />
            <div>Please check off all the symptoms and behaviors that apply to your child:</div>
            <label htmlFor="communications">Communication:</label>
            <FieldSelectList
                multiple={true}
                name="communications"
                data={communicationsValues}
                textField={'label'}
                valueField={'value'}
            />

            <label htmlFor="behaviors">Other behaviors:</label>
            <FieldSelectList
                multiple={true}
                name="behaviors"
                data={behaviorsValues}
                textField={'label'}
                valueField={'value'}
            />
            <label htmlFor="interests">Unusual Interests:</label>
            <FieldSelectList
                multiple={true}
                name="interests"
                data={interestsValues}
                textField={'label'}
                valueField={'value'}
            />
            <label htmlFor="socials">Social:</label>
            <FieldSelectList
                multiple={true}
                name="socials"
                data={socialsValues}
                textField={'label'}
                valueField={'value'}
            />
            <div className={classNames(styles.schedule)}>
                <div>
                    What is your son/daughter’s current educational and treatment schedule, including ABA and related
                    services (speech, OT, PT, tutoring, etc.)? Also include the location if not at the home.
                </div>
                <div className={classNames(styles.scheduleItemsList)}>
                    <div className={classNames(styles.scheduleTime)}>
                        <div className={classNames(styles.scheduleDayWide, styles.scheduleDayLabel)}></div>
                        <div className={classNames(styles.scheduleDayWide)}>Morning</div>
                        <div className={classNames(styles.scheduleDayWide)}>Afternoon</div>
                        <div className={classNames(styles.scheduleDayWide)}>Evening</div>
                    </div>
                    {scheduleDays.map((d: string, i: number) => {
                        return (
                            <div className={classNames(styles.scheduleTime)} key={'time' + i}>
                                <div className={classNames(styles.scheduleDayWide, styles.scheduleDayLabel)}>{d}</div>
                                {scheduleTime.map((t: string, j: number) => {
                                    return (
                                        <div key={'day_sc' + j} className={classNames(styles.scheduleDayWide)}>
                                            <FieldTextArea
                                                name={'schedule[' + t + '][' + d + ']'}
                                                wrapperConfig={{
                                                    classes: {
                                                        label: 'fullSize',
                                                    },
                                                    label: '',
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<BriefBackgroundFormData, BriefBackgroundFormProps>({
    form: 'brief-background-form',
    enableReinitialize: true,
    onSubmit: async (data: BriefBackgroundFormData, dispatch: Dispatch<any>, props: BriefBackgroundFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeBriefBackground({ ...data }, dispatch);
    },
    onChange: async (
        values: Partial<BriefBackgroundFormData>,
        dispatch: Dispatch<any>,
        props: any,
        previousValues: Partial<BriefBackgroundFormData>,
    ) => {},
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<BriefBackgroundFormData, BriefBackgroundFormProps, string>,
    ) {
        props.setIsLoader(false);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(BriefBackgroundForm);
